import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODES = ["Online", "Offline", "Hybrid"];
const ACADEMIC = ["Enrolled", "In Progress", "Completed", "Dropped", "On Hold"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const EMPTY = {
  student_id: "", course_id: "", batch_id: "", mode: "Offline",
  course_fee: 0, discount: 0, scholarship_amount: 0, waiver_amount: 0,
  admission_date: "", expected_completion_date: "", job_guarantee_eligible: false,
  academic_status: "Enrolled", placement_status: "Not Started", remarks: "",
};

export default function EnrollmentDialog({ open, onOpenChange, editRow, onSaved }) {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/students", { params: { page_size: 100 } }).then(({ data }) => setStudents(data.items || []));
    api.get("/courses", { params: { active_only: true } }).then(({ data }) => setCourses(data.items || []));
    api.get("/batches").then(({ data }) => setBatches(data.items || []));
    setForm(editRow ? { ...EMPTY, ...editRow } : EMPTY);
  }, [open, editRow]);

  const batchesForCourse = useMemo(
    () => batches.filter((b) => !form.course_id || b.course_id === form.course_id),
    [batches, form.course_id]
  );

  const finalFee = Math.max(0, Number(form.course_fee || 0) - Number(form.discount || 0) - Number(form.scholarship_amount || 0) - Number(form.waiver_amount || 0));

  const onCourse = (v) => {
    const c = courses.find((x) => x.id === v);
    setForm((f) => ({ ...f, course_id: v, batch_id: "", course_fee: c ? c.course_fee : f.course_fee }));
  };

  const seatLabel = (b) => {
    const left = (b.seats || 0) - (b.enrolled_count || 0);
    if (!b.seats) return "· open seats";
    if (left <= 0) return "· FULL";
    return `· ${left} left`;
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.student_id || !form.course_id) { toast.error("Student and course are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        course_fee: Number(form.course_fee), discount: Number(form.discount),
        scholarship_amount: Number(form.scholarship_amount), waiver_amount: Number(form.waiver_amount),
      };
      if (editRow) await api.put(`/enrollments/${editRow.id}`, payload);
      else await api.post("/enrollments", payload);
      toast.success(editRow ? "Enrollment updated" : "Enrollment created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="enrollment-dialog">
        <DialogHeader>
          <DialogTitle>{editRow ? "Edit enrollment" : "New enrollment"}</DialogTitle>
          <DialogDescription>Enroll a student into a course &amp; batch and set the fee breakdown.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Student</Label>
            <Select value={form.student_id} onValueChange={(v) => setForm((f) => ({ ...f, student_id: v }))}>
              <SelectTrigger data-testid="enrollment-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_code})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Course</Label>
            <Select value={form.course_id} onValueChange={onCourse}>
              <SelectTrigger data-testid="enrollment-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Batch</Label>
            <Select value={form.batch_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, batch_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="enrollment-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No batch yet</SelectItem>
                {batchesForCourse.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} {seatLabel(b)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger data-testid="enrollment-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Academic status</Label>
            <Select value={form.academic_status} onValueChange={(v) => setForm((f) => ({ ...f, academic_status: v }))}>
              <SelectTrigger data-testid="enrollment-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ACADEMIC.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Course fee (₹)</Label><Input type="number" value={form.course_fee} onChange={set("course_fee")} data-testid="enrollment-fee-input" /></div>
          <div><Label>Discount (₹)</Label><Input type="number" value={form.discount} onChange={set("discount")} data-testid="enrollment-discount-input" /></div>
          <div><Label>Scholarship (₹)</Label><Input type="number" value={form.scholarship_amount} onChange={set("scholarship_amount")} /></div>
          <div><Label>Waiver (₹)</Label><Input type="number" value={form.waiver_amount} onChange={set("waiver_amount")} /></div>
          <div><Label>Admission date</Label><Input type="date" value={form.admission_date} onChange={set("admission_date")} /></div>
          <div><Label>Expected completion</Label><Input type="date" value={form.expected_completion_date} onChange={set("expected_completion_date")} /></div>
          <div className="col-span-2 flex items-center justify-between rounded-lg border border-border bg-slate-50 p-3">
            <span className="text-sm text-muted-foreground">Final payable fee</span>
            <span className="font-display text-xl font-bold text-secondary" data-testid="enrollment-final-fee">{inr(finalFee)}</span>
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.job_guarantee_eligible} onCheckedChange={(v) => setForm((f) => ({ ...f, job_guarantee_eligible: v }))} data-testid="enrollment-jg-switch" />
            <Label>Job guarantee eligible</Label>
          </div>
          <div className="col-span-2"><Label>Remarks</Label><Textarea value={form.remarks} onChange={set("remarks")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-enrollment-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
